import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../app/hooks'
import { selectFilteredContacts } from '../../features/customer/customerSlice'
import { saveOrder, sendQuote } from '../../features/order/orderSlice'
import EmailSearch from './EmailSearch'
import styles from './SendQuoteModal.module.scss'
import { BUTTON_TYPES, AWCButton } from '../../GlobalComponents/Inputs/Inputs'
import { useDispatch } from 'react-redux'
import { Input as MaterialInput } from '@material-ui/core'
import { Checkbox } from '@material-ui/core'

import Scrollbars from 'react-custom-scrollbars-2'
import { getAttachmentDownloadURL } from '../../features/order/orderAPI'
import RecipientModal, { AddContactModal } from './RecipientModal'
import { Splitter, SplitterOnChangeEvent } from '@progress/kendo-react-layout'
import { getCurrentUser } from '../../features/meta/metaSlice'

import { Editor, EditorTools, EditorUtils, ProseMirror } from '@progress/kendo-react-editor'
import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'
import { getAPIURL } from '../../GlobalComponents/URLs'

const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    ForeColor,
    BackColor,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
} = EditorTools

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        // border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

const makeDefaultBody = async (contact: ContactCustomerAccountCodeViewRow) => {
    return `<div style="font-family: Calibri;"><p>${contact.FirstName.trim()} ${contact.LastName.trim()},<br><br>Thank you for working with AWC.
    <br>Please find our interactive quotation attached and let us know if we can help in any other way.<br><br>We appreciate your business!
    <br><br></p></div>`
}
interface SendQuoteModalProps {
    close(): void
}
export const SendQuoteModal = (props: SendQuoteModalProps) => {
    const classes = useStyles()
    const { close } = props
    let toContact = useAppSelector((state) => state.order.toContact)!
    const [tos, setTos] = useState<(string | ContactCustomerAccountCodeViewRow)[]>([toContact])
    const [body, setBody] = useState<any>()
    let customer = useAppSelector((state) => state.order.currentCustomer)!
    let order = useAppSelector((state) => state.order.orderHeader[0])!
    let currentAccount = useAppSelector((state) => state.order.currentAccount)!
    let ccContacts = useAppSelector((state) => state.order.ccContacts)
    const [ccs, setCcs] = useState(ccContacts)
    const contacts = useAppSelector((state) =>
        selectFilteredContacts(state, state.order.currentAccount!.AccountCode, '')
    )
    const _attachments = useAppSelector((state) => state.order.orderFilenames)
    const attachments = _attachments.filter((row) => !row.Internal)
    const [files, setFiles] = useState(attachments)
    const [recipientOpen, setRecipientOpen] = useState(false)
    const [recipientMode, setRecipientMode] = useState<'To' | 'Cc'>('To')
    const [addContactModalOpen, setAddContactModalOpen] = useState(false)
    const dispatch = useDispatch()
    const [subject, setSubject] = useState('')
    const title = `Send Quote #${order.OrderNumber} Cust: ${customer.AccountCode}`
    const [ccVisible, setCcVisible] = useState(ccContacts.length > 0)
    const [bccVisible, setBccVisible] = useState(false)
    const [bccs, setBccs] = useState<(string | ContactCustomerAccountCodeViewRow)[]>([])
    const [loading, setLoading] = useState(true)
    const [nestedPanes, setNestedPanes] = useState<Array<any>>([{ size: '40%' }, {}])
    const editor = useRef<Editor | null>(null)
    const [toInvalid, setToInvalid] = useState(false)
    const [ccInvalid, setCcInvalid] = useState(false)
    const [bccInvalid, setBccInvalid] = useState(false)

    const dataSheetParts = useAppSelector((state) => {
        let toInclude = state.order.orderHeader[0].OrderDetailView.filter(
            (row) => row.includeDataSheets
        )
        let processed = toInclude.map((row) => ({
            partKey: row.PartKey!,
            partNumber: row.PartNumber!,
        }))
        let alreadyIn: Record<number, boolean> = {}
        let clean = processed.filter((row) => row.partKey && row.partNumber)
        let final: typeof clean = []
        clean.forEach((row) => {
            if (alreadyIn[row.partKey]) {
                return
            }
            alreadyIn[row.partKey] = true
            final.push(row)
        })
        return final
    })

    const [dataSheets, setDataSheets] = useState<any[]>([])
    // TODO this is done on line item change now for graying out input -- can just get it from there
    useEffect(() => {
        const getDataSheets = async () => {
            if (dataSheetParts.length === 0) {
                return
            }
            let dataSheets = await Promise.all(
                dataSheetParts.map(async (info) => {
                    let url = getAPIURL() + 'inventory/parts/datasheets/' + info.partKey
                    let req = await fetch(url, { credentials: 'include' })
                    let json = await req.json()
                    return { DataSheets: json, name: `${info.partNumber}` }
                })
            )
            let currentUser = await getCurrentUser()
            let company = currentUser.CompanyCode
            if (company !== 'A' && company !== 'C') {
                fqAlert({
                    type: 'error',
                    title: 'Invalid Company',
                    message: `Can't get data sheets for company code ${company}`,
                })
                return
            }
            let baseUrl =
                currentUser.CompanyCode === 'C'
                    ? 'https://www.a3im.com'
                    : 'https://www.awc-inc.com/'
            let flattenedDataSheets: Array<{ url: string; name: string }> = []
            dataSheets.forEach((row) => {
                if (row.DataSheets.length === 0) {
                    return
                }
                if (row.DataSheets.length > 1) {
                    row.DataSheets.forEach((sheet: string, i: number) => {
                        flattenedDataSheets.push({
                            url: baseUrl + sheet,
                            name: row.name + ` - ${i + 1}`,
                        })
                    })
                } else {
                    flattenedDataSheets.push({
                        url: baseUrl + row.DataSheets[0],
                        name: row.name,
                    })
                }
            })
            setDataSheets(flattenedDataSheets)
        }
        getDataSheets()
    }, [])
    const onNestedChange = (event: SplitterOnChangeEvent) => {
        setNestedPanes(event.newState)
    }
    //probably should just return the new state from the saveOrder dispatch and initialize in one use effect
    useEffect(() => {
        ;(async function () {
            await dispatch(saveOrder(null))
            setLoading(false)
        })()
    }, [])
    useEffect(() => {
        if (!loading) {
            ;(async function () {
                let newBody = await makeDefaultBody(toContact)
                setFiles(attachments)
                setSubject(
                    `AWC Quote # ${order.OrderNumber}` +
                        (order.RFQNumber ? ` for Reference # ${order.RFQNumber}` : '')
                )
                setBody(
                    EditorUtils.createDocument(
                        new ProseMirror.Schema({
                            nodes: EditorUtils.nodes,
                            marks: EditorUtils.marks,
                        }),
                        newBody
                    )
                )
                setNestedPanes([{ size: Math.min(_attachments.length, 4) * 35 }, {}])
            })()
        }
    }, [loading])
    return (
        <Modal
            open={true}
            className={classes.modal}
            // reason will be populated if they clicked off the modal to close
            onClose={(e: any, reason: string) => !reason && close()}
            BackdropComponent={Backdrop}
            // kendo uses 99 for dropdowns
            style={{ zIndex: 88 }}
            // !!! kendo dropdowns will instantly close if this isn't disabled. The drop down renders outside the modal and takes focus out, but the focus trap will take it back
            disableEnforceFocus
        >
            <div className={styles.modalContainer}>
                {loading && (
                    <div className={styles.lineItemNotesModal} style={{ minWidth: 800 }}>
                        {' '}
                        <div className={'centered'} style={{ height: '100%' }}>
                            <div className={'k-i-loading'} style={{ fontSize: 200 }} />
                        </div>
                    </div>
                )}
                {!loading && (
                    <div
                        className={styles.lineItemNotesModal}
                        data-testid={'lineItemNotesModal'}
                        style={{ minWidth: 800, display: 'flex', flexDirection: 'column' }}
                    >
                        {/* Line Item Notes Header */}
                        <div className={styles.lineItemNotesModalHeader}>
                            <div className={styles.lineItemNotesModalTitle}>{title}</div>
                            <div onClick={close} className={'a-i-close'} />
                        </div>
                        <div className={'a-divider'} style={{ marginBottom: 15 }} />

                        {/*  */}
                        <div>
                            <div className={styles.addressInfo}>
                                <div
                                    className={'centered'}
                                    style={{ marginRight: 10, marginBottom: 4 }}
                                >
                                    <AWCButton
                                        type={'secondary'}
                                        onClick={() => {
                                            setRecipientMode('To')
                                            setRecipientOpen(true)
                                        }}
                                        style={{ width: 42, height: 31 }}
                                        data-testid={'toButton'}
                                    >
                                        To:
                                    </AWCButton>
                                </div>
                                <div style={{ position: 'relative', display: 'inline-grid' }}>
                                    <EmailSearch
                                        contacts={contacts}
                                        onCreateNewContact={() => {
                                            setRecipientMode('To')
                                            setAddContactModalOpen(true)
                                        }}
                                        selectedContacts={tos}
                                        setSelectedContacts={(newContacts) => {
                                            setTos(newContacts)
                                            setToInvalid(false)
                                        }}
                                        multiple
                                        style={{ width: '75%' }}
                                        data-testid={'toField'}
                                        invalidOffset={45}
                                        onInvalid={() => setToInvalid(true)}
                                    />
                                    <div style={{ position: 'absolute', right: 0, top: 0 }}>
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            <AWCButton
                                                type={'secondary'}
                                                onClick={() => {
                                                    setCcVisible(!ccVisible)
                                                }}
                                                style={{ width: 42, height: 31 }}
                                                data-testid={'toButton'}
                                            >
                                                Cc
                                            </AWCButton>
                                            <AWCButton
                                                type={'secondary'}
                                                onClick={() => {
                                                    setBccVisible(!bccVisible)
                                                }}
                                                style={{ width: 42, height: 31 }}
                                                data-testid={'toButton'}
                                            >
                                                Bcc
                                            </AWCButton>
                                        </div>
                                    </div>
                                </div>
                                {ccVisible && (
                                    <>
                                        <div className={'centered'} style={{ marginRight: 10 }}>
                                            <AWCButton
                                                type={'secondary'}
                                                onClick={() => {
                                                    setRecipientMode('Cc')
                                                    setRecipientOpen(true)
                                                }}
                                                data-testid={'ccButton'}
                                                style={{ width: 42, height: 31 }}
                                            >
                                                Cc:
                                            </AWCButton>
                                        </div>
                                        <EmailSearch
                                            contacts={contacts}
                                            onCreateNewContact={() => {
                                                setRecipientMode('Cc')
                                                setAddContactModalOpen(true)
                                            }}
                                            selectedContacts={ccs}
                                            setSelectedContacts={(newContacts) => {
                                                setCcs(newContacts)
                                                setCcInvalid(false)
                                            }}
                                            multiple
                                            style={{ width: '100%' }}
                                            data-testid={'ccField'}
                                            onInvalid={() => setCcInvalid(true)}
                                        />
                                    </>
                                )}

                                {bccVisible && (
                                    <>
                                        <div className={'centered'} style={{ marginRight: 10 }}>
                                            <AWCButton
                                                type={'secondary'}
                                                onClick={() => {}}
                                                data-testid={'ccButton'}
                                                style={{ width: 42, height: 31 }}
                                            >
                                                Bcc:
                                            </AWCButton>
                                        </div>
                                        <EmailSearch
                                            contacts={contacts}
                                            onCreateNewContact={() => {
                                                setRecipientMode('Cc')
                                                setAddContactModalOpen(true)
                                            }}
                                            selectedContacts={bccs}
                                            setSelectedContacts={(newContacts) => {
                                                setBccs(newContacts)
                                                setBccInvalid(false)
                                            }}
                                            multiple
                                            style={{ width: '100%' }}
                                            data-testid={'ccField'}
                                            onInvalid={() => setBccInvalid(true)}
                                        />
                                    </>
                                )}

                                <div
                                    className={'centered'}
                                    style={{ marginRight: 10, fontWeight: 500 }}
                                >
                                    Subject:
                                </div>
                                <MaterialInput
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                />
                            </div>
                            <div
                                style={{
                                    marginTop: 20,
                                    marginBottom: 5,
                                    padding: '0 2rem',
                                    fontWeight: 500,
                                }}
                            >
                                Attachments ({files.length})
                            </div>
                            <div>
                                <div className={styles.attachmentsHeader}>
                                    <div
                                        className={styles.columnHeader}
                                        style={{
                                            justifyContent: 'center',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div>Include</div>
                                        <div>with Email</div>
                                    </div>

                                    <div className={styles.columnHeader}>File Name</div>
                                </div>
                            </div>
                        </div>
                        {/*  */}
                        <Splitter
                            style={{
                                flex: 1,
                                borderRadius: 10,
                                height: '40%',
                            }}
                            panes={nestedPanes}
                            orientation={'vertical'}
                            onChange={onNestedChange}
                        >
                            <Scrollbars
                                className={'scroll-bar'}
                                renderTrackHorizontal={() => <div />}
                                autoHeight
                                autoHeightMin={0}
                                autoHeightMax={315}
                                height={315}
                            >
                                <div className={styles.attachmentsListContainer}>
                                    {files.map((row) => (
                                        <div className={styles.attachmentsRow}>
                                            <div style={{ textAlign: 'center' }}>
                                                <Checkbox
                                                    checked={row.IncludeWithOrder}
                                                    style={
                                                        row.IncludeWithOrder
                                                            ? { color: 'var(--linkblue)' }
                                                            : {}
                                                    }
                                                    onChange={(
                                                        event: ChangeEvent<HTMLInputElement>
                                                    ) => {
                                                        setFiles((files) =>
                                                            files.map((row2) =>
                                                                row2.FileId === row.FileId
                                                                    ? {
                                                                        ...row2,
                                                                        IncludeWithOrder:
                                                                              event.target.checked,
                                                                    }
                                                                    : { ...row2 }
                                                            )
                                                        )
                                                    }}
                                                    data-testid="attachmentIncludeWithEmailCheckbox"
                                                />
                                            </div>

                                            <div>
                                                <div
                                                    className={styles.attachmentLinks}
                                                    onClick={() => {
                                                        window.location.href =
                                                            getAttachmentDownloadURL(row)
                                                    }}
                                                >
                                                    {row.FriendlyFilename}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Scrollbars>

                            <div
                                style={{
                                    display: 'flex',
                                    marginBottom: 50,
                                    marginTop: 15,
                                    gap: 5,
                                    height: '92%',
                                    flexDirection: 'column',
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 500,
                                            marginRight: 5,
                                            fontSize: 16,
                                            display: 'inline-block',
                                        }}
                                    >
                                        Data Sheets ({dataSheets.length}):
                                    </div>
                                    {dataSheets.map((row) => (
                                        <AWCButton
                                            type="secondary"
                                            style={{
                                                height: 31,
                                                marginLeft: 5,
                                                lineHeight: '19px',
                                                fontSize: 14,
                                                width: 'fit-content',
                                                display: 'inline-block',
                                            }}
                                            onClick={() => window.open(row.url)}
                                        >
                                            {row.name}
                                        </AWCButton>
                                    ))}
                                </div>

                                <div style={{ display: 'flex', flex: 1 }}>
                                    <div style={{ fontWeight: 500, marginRight: 5, fontSize: 16 }}>
                                        Body:
                                    </div>

                                    {body !== undefined && (
                                        <Editor
                                            tools={[
                                                [Bold, Italic, Underline, Strikethrough],
                                                ForeColor,
                                                BackColor,
                                                [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                                                [Indent, Outdent],
                                                [OrderedList, UnorderedList],
                                                [Undo, Redo],
                                            ]}
                                            contentStyle={{ height: '100%' }}
                                            style={{ width: '100%', height: '100%' }}
                                            ref={editor}
                                            value={body}
                                            onChange={(e) => {
                                                setBody(e.value)
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </Splitter>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '110px 110px',
                                marginTop: 10,
                                justifyContent: 'end',
                                padding: '0 2rem',
                            }}
                        >
                            <div
                                className={'a-cancel-button'}
                                style={{
                                    width: 94,
                                    // visibility: singleChoice && !toContact ? 'hidden' : 'visible',
                                }}
                                onClick={close}
                                data-testid="lineItemNotesModalCancelButton"
                            >
                                Cancel
                            </div>
                            <AWCButton
                                type={BUTTON_TYPES.PRIMARY}
                                // onClick={onConfirmClick}
                                onClick={async () => {
                                    if (!tos.length) {
                                        fqAlert({
                                            type: 'error',
                                            title: 'You must specify a "To" address',
                                        })
                                        return
                                    }
                                    if (bccInvalid || ccInvalid || toInvalid) {
                                        fqAlert({ type: 'error', title: 'Invalid Email' })
                                        return
                                    }
                                    let emailBody = ''
                                    if (editor.current) {
                                        const view = editor.current.view
                                        if (view) {
                                            emailBody = EditorUtils.getHtml(view.state)
                                        }
                                    }
                                    if (!emailBody) {
                                        return
                                    }
                                    dispatch(
                                        sendQuote({
                                            body: emailBody,
                                            subject,
                                            tos,
                                            currentUser: await getCurrentUser(),
                                            orderTo: toContact,
                                            ccs,
                                            bccs,
                                            files,
                                        })
                                    )
                                    close()
                                }}
                                style={{
                                    width: 94,
                                }}
                                data-testid="lineItemNotesModalSubmitButton"
                            >
                                Save
                            </AWCButton>
                        </div>
                    </div>
                )}
                <Modal
                    open={recipientOpen || addContactModalOpen}
                    className={classes.modal}
                    onClose={(e: any, reason: string) => {
                        if (!reason) {
                            setRecipientOpen(false)
                            setAddContactModalOpen(false)
                        }
                    }}
                    aria-labelledby="simple-modal-title"
                    aria-describedby="simple-modal-description"
                    BackdropComponent={Backdrop}
                >
                    <div>
                        {recipientOpen && (
                            <RecipientModal
                                close={() => setRecipientOpen(false)}
                                onCancel={() => setRecipientOpen(false)}
                                submitContacts={(
                                    ccContacts: ContactCustomerAccountCodeViewRow[]
                                ) => {
                                    recipientMode === 'To' ? setTos(ccContacts) : setCcs(ccContacts)
                                    setRecipientOpen(false)
                                }}
                                selectedContacts={recipientMode === 'To' ? tos : ccs}
                                // TODO fix -- don't use singlechoice to decide
                                // these names aged poorly since multiple to's are allowed now
                                singleChoice={false}
                                required={recipientMode === 'To'}
                                reason={recipientMode}
                            />
                        )}
                        {addContactModalOpen && (
                            <AddContactModal
                                onSuccess={(contact: ContactCustomerAccountCodeViewRow) => {
                                    // always multiple now
                                    if (recipientMode === 'To') setTos([...tos, contact])
                                    else setCcs([...ccContacts, contact])
                                    setAddContactModalOpen(false)
                                }}
                                onCancelClick={() => setAddContactModalOpen(false)}
                                customer={currentAccount}
                            />
                        )}
                    </div>
                </Modal>
            </div>
        </Modal>
    )
}
